import { useRef, useState } from "react";
import { supabase } from "../supabase";
import Toast from "./Toast";
import { createNotification } from "../utils/notifications";
import { confirmDialog } from "../utils/dialog";
import { DESIGN_FILES_BUCKET, DOWNLOAD_LINK_SECONDS } from "../utils/designFiles";
import { makeZip } from "../utils/makeZip";
import { PiDownloadSimpleBold } from "react-icons/pi";

// Zipping holds every file in the phone's memory at once (nothing is streamed to disk).
// A post can have up to 12 files at up to 20MB each — a combined size past this is rare
// (real design files usually run a few KB to a few hundred KB) but big enough that an
// older or low-memory phone could hang or crash making the zip. Past this, a confirm
// dialog names the size and asks first, rather than silently risking a crash.
const ZIP_WARN_BYTES = 100 * 1024 * 1024; // 100 MB

// Controlled component — see LikeButton.jsx for why: the parent page owns
// `downloads` (part of its `designs` state) via one shared realtime channel
// per page instead of one per button.
export default function DownloadButton({
  fileURL,
  files,
  designId,
  downloads,
  designOwnerId,
  user,
  onUpdate
}) {
  const fileList =
    files && files.length > 0
      ? files
      : fileURL
      ? [{ name: null, url: fileURL }]
      : [];

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const countedRef = useRef(false); // the download count goes up once per tap
  // disabled={loading} only takes effect on the next render — a very fast double-tap can
  // fire a second handleDownload before that paints, which would otherwise start the
  // download (or build the zip) twice (same class of bug already guarded elsewhere, e.g.
  // Upload.jsx, ImageCropModal.jsx).
  const actionRef = useRef(false);

  // The address to download a file from. Newer files are private (they have a `path`):
  // Supabase makes a link for them that works for DOWNLOAD_LINK_SECONDS, and only for a
  // logged-in person. Older posts still have an ordinary public link.
  async function linkFor(file) {
    if (!file.path) return { url: file.url, signed: false };

    const { data, error } = await supabase.storage
      .from(DESIGN_FILES_BUCKET)
      .createSignedUrl(file.path, DOWNLOAD_LINK_SECONDS, { download: file.name || true });

    if (error || !data?.signedUrl) throw error || new Error("No download link");
    return { url: data.signedUrl, signed: true };
  }

  // The name a file should be saved under.
  //
  // The fallback (no `name`, used only for older posts with just a bare design_url and no
  // design_files entry) pulls the name from the storage URL itself, which is
  // percent-encoded — decode it back so "my%20design" downloads as "my design", not the
  // encoded form.
  function fileNameFor(file) {
    if (file.name) return file.name;
    let fallback = (file.url || "").split("/").pop().split("?")[0] || "design";
    try {
      fallback = decodeURIComponent(fallback);
    } catch {
      // Malformed escape sequence — keep the undecoded form rather than throw.
    }
    return fallback;
  }

  function clickDownloadLink(href, filename) {
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // One file: straight from Storage. Supabase Storage honours a `download` query param by
  // sending Content-Disposition: attachment, so the browser downloads the file directly
  // (no blob: URL needed — Chrome blocks blob downloads triggered from insecure/http
  // pages, e.g. local network testing). A signed link already carries its download name.
  function saveFileToDevice(url, file, signed) {
    const filename = fileNameFor(file);
    if (signed) {
      clickDownloadLink(url, filename);
      return;
    }
    const separator = url.includes("?") ? "&" : "?";
    clickDownloadLink(`${url}${separator}download=${encodeURIComponent(filename)}`, filename);
  }

  // The combined size of the files, in bytes, found out WITHOUT downloading them (a HEAD
  // request per file — just its headers). null if that couldn't be worked out for one or
  // more files (an old file's server may not report a length): rather than block on a
  // guess, the warning below is simply skipped in that case.
  async function totalZipBytes() {
    try {
      const sizes = await Promise.all(
        fileList.map(async file => {
          const { url } = await linkFor(file);
          const res = await fetch(url, { method: "HEAD" });
          const length = res.headers.get("content-length");
          if (!res.ok || length === null) throw new Error("unknown size");
          return Number(length);
        })
      );
      return sizes.reduce((sum, n) => sum + n, 0);
    } catch {
      return null;
    }
  }

  // Several files: fetched and put in ONE zip, which is a single download. Browsers
  // (Android Chrome especially) refuse to start many downloads from one tap, and even
  // separate taps can end up silently dropped by the phone's own download manager — a
  // post's files always go out as exactly one download, so there's nothing to drop.
  async function saveAllAsZip() {
    const parts = [];
    for (const file of fileList) {
      const { url } = await linkFor(file);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Could not read ${fileNameFor(file)} (${res.status})`);
      parts.push({ name: fileNameFor(file), bytes: new Uint8Array(await res.arrayBuffer()) });
    }

    const objectUrl = URL.createObjectURL(makeZip(parts));
    clickDownloadLink(objectUrl, `GatherYou-design-${String(designId).slice(0, 8)}.zip`);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  // The download counter (one atomic +1 on the server) and the notification to the owner.
  async function recordDownload() {
    if (countedRef.current) return;
    countedRef.current = true;

    // Atomic UPDATE...RETURNING via RPC — two downloads landing at the same
    // moment used to both read the same starting count and overwrite each
    // other's +1 (classic read-then-write race), silently losing a count.
    const { data: updatedDownloads, error } = await supabase.rpc("increment_design_downloads", {
      target_design_id: designId
    });

    if (error) {
      console.log(error);
      return;
    }

    onUpdate?.({ downloads: updatedDownloads });
    createNotification({ userId: designOwnerId, actorId: user.id, type: "download", designId });
  }

  async function handleDownload() {
    if (!user) {
      setToast("Please log in to download");
      return;
    }

    if (fileList.length === 0 || loading || actionRef.current) return;
    actionRef.current = true;
    setLoading(true);
    countedRef.current = false;

    if (fileList.length === 1) {
      try {
        const { url, signed } = await linkFor(fileList[0]);
        saveFileToDevice(url, fileList[0], signed);
      } catch (err) {
        console.log("DOWNLOAD ERROR:", err);
        setToast("Couldn't download. Please try again.");
        setLoading(false);
        actionRef.current = false;
        return;
      }
      setToast("Download started");
    } else {
      setToast("Checking file sizes…");
      const totalBytes = await totalZipBytes();
      if (totalBytes !== null && totalBytes > ZIP_WARN_BYTES) {
        setToast("");
        const totalMB = Math.round(totalBytes / (1024 * 1024));
        const ok = await confirmDialog({
          title: "Large download",
          message: `These files add up to about ${totalMB} MB zipped into one file. That can be slow, or fail, on an older or low-storage phone. Continue?`,
          confirmText: "Download anyway"
        });
        if (!ok) {
          setLoading(false);
          actionRef.current = false;
          return;
        }
      }

      setToast("Preparing your files…");
      try {
        await saveAllAsZip();
      } catch (err) {
        console.log("DOWNLOAD ERROR:", err);
        setToast("Couldn't download. Please try again.");
        setLoading(false);
        actionRef.current = false;
        return;
      }
      setToast(`Downloaded ${fileList.length} files in one zip`);
    }

    setLoading(false);
    actionRef.current = false;
    await recordDownload();
  }

  if (fileList.length === 0) return null;

  return (
    <>
      <button
        onClick={handleDownload}
        disabled={loading}
        style={{
          ...btn,
          opacity: loading ? 0.6 : 1
        }}
      >
        <PiDownloadSimpleBold size={27} />
        <span>{loading ? "..." : downloads || 0}</span>
      </button>

      {toast && (
        <Toast
          text={toast}
          onClose={() => setToast("")}
        />
      )}
    </>
  );
}

const btn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  height: 40,
  padding: "0 2px",
  borderRadius: 20,
  border: "none",
  cursor: "pointer",
  background: "none",
  color: "var(--text-primary)",
  fontWeight: 700,
  fontSize: 14,
};
